import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Observable, PureComputed, SubscribableOrNullableValue } from 'knockout';
import { GenericAnimator } from 'managers/genericAnimator';
import { NodeTracker as Tracker } from 'managers/nodes/tracker';
import { EffectsContainer } from 'mixins/withEffects';
import './slidingContainer.html';
import './slidingContainer.scss';

export interface SlidingContainerVMConfig {
    expand: SubscribableOrNullableValue<boolean>
    animationDuration: SubscribableOrNullableValue<number>
    animateInitial: SubscribableOrNullableValue<boolean>
    renderOnExpand: SubscribableOrNullableValue<boolean>
}

interface SlidingContainerVMFrameState {
    contentHeight: number
    contentOffset?: number
    bodyHeight?: number
    isExpanded: boolean
}

@injectable()
export default class SlidingContainerVM{
    private isInitialExpand: Observable<boolean>
    private isBodyNeeded = ko.observable(false);

    contentNode = ko.observable<HTMLElement>();

    bodyHeight = ko.observable(0);
    contentOffset = ko.observable(0);

    isBodyRendered: PureComputed<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: SlidingContainerVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const isExpanded = ko.flattenComputed(config.expand, false);
        const animationDuration = ko.flattenComputed(config.animationDuration, 450);
        const animateInitial = ko.flattenComputed(config.animateInitial, false);
        const renderOnExpand = ko.flattenComputed(config.renderOnExpand, false);

        const { height: contentHeight } = new Tracker({ node: this.contentNode });

        this.isInitialExpand = ko.observable(<boolean>true);
        this.isBodyRendered = ko.pureComputed(() => !renderOnExpand() || this.isBodyNeeded());

        effects.register(height => {
            if (height == 0)
                this.isInitialExpand(true);
        }, [contentHeight]);

        effects.register(new GenericAnimator<SlidingContainerVMFrameState>({
            duration: animationDuration,

            state: ko.pureComputed(() => ({
                contentHeight: contentHeight(),
                isExpanded: isExpanded()
            })),

            frame: ({ state, progress }) => {
                if (progress == 0) {
                    _(state).extend({
                        contentOffset: this.contentOffset(),
                        bodyHeight: this.bodyHeight()
                    });
                }

                const { contentHeight, isExpanded } = state;
                const contentOffset = <number>state.contentOffset;
                const bodyHeight = <number>state.bodyHeight;

                if (progress == 0 && isExpanded)
                    this.isBodyNeeded(true);

                const heightDiff = isExpanded ? contentHeight - bodyHeight : -bodyHeight;
                const offsetDiff = isExpanded ? -contentOffset : -bodyHeight - contentOffset

                const skipAnimation = this.isInitialExpand() && !animateInitial() ||
                    heightDiff == 0 && offsetDiff == 0;

                if (skipAnimation)
                    progress = 100;

                if (isExpanded && this.isInitialExpand() && contentHeight > 0 && progress == 100)
                    this.isInitialExpand(false);

                this.bodyHeight(bodyHeight + heightDiff * progress / 100);
                this.contentOffset(contentOffset + offsetDiff * progress / 100);                

                if (progress == 100 && !isExpanded)
                    this.isBodyNeeded(false);

                return skipAnimation;
            }            
        }));

        ko.applyBindingsToNode(info.element, {
            'css': {
                'sliding-container': true
            }
        });
    }
}